import React from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Receipt } from "lucide-react";

import { formatCurrency, formatInt, formatPct } from "../lib/formato";
import { faixaDatas } from "../lib/periodo";
import BarraResultado, { corMargem } from "./BarraResultado";

function Bloco({ rotulo, valor, detalhe, cor }) {
  return (
    <div className="min-w-0">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{rotulo}</p>
      <p className={`text-xl sm:text-2xl font-black tabular-nums leading-tight ${cor}`}>{valor}</p>
      {detalhe && <p className="text-[11px] text-muted-foreground tabular-nums">{detalhe}</p>}
    </div>
  );
}

// Faixa do topo: a conta inteira dos módulos no período, da venda ao lucro.
export default function ResumoModulos({ dados }) {
  const navigate = useNavigate();
  const t = dados.totais;
  const pct = (v) => (t.faturamento > 0 ? `${formatPct((v / t.faturamento) * 100)} das vendas` : null);
  const sem = dados.despesas_sem_modulo || { valor: 0, quantidade: 0 };
  return (
    <div className="rounded-2xl border bg-gradient-to-br from-primary/10 via-card to-emerald-500/10 p-5 sm:p-6" data-e2e="resumo-modulos">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <h2 className="text-lg font-bold">Resultado dos módulos</h2>
        <span className="text-xs text-muted-foreground">· {faixaDatas(dados.periodo)}</span>
        <span className={`ml-auto text-sm font-bold px-2.5 py-1 rounded-lg tabular-nums ${corMargem(t.margem_pct)}`}>
          {t.margem_pct == null ? "sem vendas" : `Margem ${formatPct(t.margem_pct)}`}
        </span>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <Bloco rotulo="Vendas" valor={formatCurrency(t.faturamento)} detalhe={`${formatInt(t.pedidos)} pedidos · ticket ${formatCurrency(t.ticket_medio)}`} cor="" />
        <Bloco rotulo="(−) CMV" valor={formatCurrency(t.cmv)} detalhe={pct(t.cmv)} cor="text-red-600 dark:text-red-400" />
        <Bloco rotulo="(−) Despesas dos módulos" valor={formatCurrency(t.despesas)} detalhe={pct(t.despesas)} cor="text-amber-600 dark:text-amber-400" />
        <Bloco rotulo="(=) Lucro" valor={formatCurrency(t.lucro)} detalhe={pct(t.lucro)}
          cor={t.lucro >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"} />
      </div>
      <BarraResultado m={t} altura="h-3.5" legenda />
      <div className="mt-4 grid gap-2 text-xs">
        {t.vendas_sem_custo > 0.009 && (
          <p className="flex gap-1.5 text-amber-800 dark:text-amber-300">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
            <span><b>{formatCurrency(t.vendas_sem_custo)}</b> vendidos em itens sem ficha técnica entram com custo zero — o lucro real é menor que o mostrado.</span>
          </p>
        )}
        <button type="button" onClick={() => navigate("/admin/financeiro/despesas")} data-e2e="despesas-sem-modulo"
          className="flex gap-1.5 text-left text-muted-foreground hover:text-foreground transition-colors">
          <Receipt className="w-3.5 h-3.5 shrink-0 mt-px" />
          <span>
            Despesas sem módulo no período: <b className="tabular-nums">{formatCurrency(sem.valor)}</b> ({formatInt(sem.quantidade)}) —
            ficam fora do lucro dos módulos. Para atribuir, edite a despesa em Financeiro → Despesas →
          </span>
        </button>
      </div>
    </div>
  );
}
